import React, { useCallback, useEffect } from 'react';

import { Button } from 'antd';

import MyCollapse from 'components/Collapse';
import CopyText from 'v2/components/Shared/CopyText';
import type { IContentDelivery } from 'v2/interfaces/product';
import type { ShoppingDelivery } from 'v2/interfaces/shopping';
import { dateService } from 'v2/services/date';
import productService from 'v2/services/product';

import BankSlipInstallment from './bankSlipIntallment';
import { BuyerDetails } from './buyerDetails';
import GetCcbTerm from './CcbTerm/getCcbTerm';
import { ChangePaymentMethod } from './changePaymentMethod';
import FiscalDocument from './fiscalDocument';
import InvoiceDetails from './invoiceDetails';
import { PaymentHistory } from './paymentHistory';

interface JobProductProps {
  delivery: ShoppingDelivery;
  closeQuickView: () => void;
}

const JobProduct = ({ delivery, closeQuickView }: JobProductProps) => {
  const [contentDelivery, setContentDelivery] = React.useState<IContentDelivery>({
    delivery_nutror: false,
    download_hash: false
  });

  useEffect(() => {
    const fetchContentDelivery = async () => {
      setContentDelivery(await productService.getContentDelivery(delivery.product.id));
    };

    fetchContentDelivery();
  }, [delivery.product.id]);

  const handleClickDownload = useCallback((hash: string | boolean) => {
    window.open('/downloads/' + hash, '_blank');
  }, []);

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-3 sm:flex-row'>
        <div
          className='h-28 w-28 max-w-28 shrink-0 rounded bg-neutral-50 bg-cover bg-center bg-no-repeat'
          style={{ backgroundImage: `url(${delivery.product.image})` }}
        />

        <div>
          <h2 className='mb-4 text-base'>
            {delivery.product.title}
            {delivery.order.status && (
              <span className='text-success block text-sm font-normal uppercase'>{delivery.order.status}</span>
            )}
          </h2>
        </div>
      </div>

      <BuyerDetails delivery={delivery} />

      <div className='flex flex-col gap-1'>
        <div className='text-sm'>
          <span className='font-semibold'>Criação:</span>{' '}
          {dateService.dateToString(new Date(delivery.created_at), 'dd/MM/yyyy HH:mm')}
        </div>
        <div className='text-sm'>
          <span className='font-semibold'>Previsão de entrega:</span>{' '}
          {delivery.order.expected_date
            ? dateService.dateToString(new Date(delivery.order.expected_date), 'dd/MM/yyyy HH:mm')
            : '-'}
        </div>
        <div className='text-sm'>
          <span className='font-semibold'>Entrega:</span>{' '}
          {delivery.order.delivery_date
            ? dateService.dateToString(new Date(delivery.order.delivery_date), 'dd/MM/yyyy HH:mm')
            : '-'}
        </div>
      </div>

      {!delivery.unlimited_installment && !delivery?.bankslip_installments && <InvoiceDetails delivery={delivery} />}

      {!delivery.unlimited_installment && delivery?.bankslip_installments && (
        <BankSlipInstallment delivery={delivery} />
      )}

      {delivery.unlimited_installment && <PaymentHistory delivery={delivery} />}

      <FiscalDocument delivery={delivery} />

      {contentDelivery.download_hash && (
        <Button type='primary' onClick={() => handleClickDownload(contentDelivery.download_hash)} block>
          Download Materiais
        </Button>
      )}

      {delivery.unlimited_installment && <ChangePaymentMethod delivery={delivery} closeQuickView={closeQuickView} />}

      <MyCollapse title='Contato com Produtor' arrow>
        <div className='flex flex-col gap-1'>
          {delivery.producer.name && (
            <div className='text-sm'>
              <span className='font-semibold'>Nome:</span> {delivery.producer.name}
            </div>
          )}
          {delivery.producer.email && (
            <div className='text-sm'>
              <span className='font-semibold'>E-mail:</span> <CopyText value={delivery.producer.email} />
            </div>
          )}
          {delivery.producer.phone && (
            <div className='text-sm'>
              <span className='font-semibold'>Telefone:</span> <CopyText value={delivery.producer.phone} />
            </div>
          )}
          {delivery.producer.address && (
            <div className='text-sm'>
              <span className='font-semibold'>Endereço:</span> {delivery.producer.address}
            </div>
          )}
        </div>
      </MyCollapse>

      <GetCcbTerm delivery={delivery} />
    </div>
  );
};

export default JobProduct;
